import PropTypes from 'prop-types';
import {
  ExclamationTriangleIcon,
  LightBulbIcon,
  AcademicCapIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';
import { useState } from 'react';

/**
 * SkillGapReport - Surfaces missing skills with actionable suggestions
 * Designed as a prominent monetization hook
 */
const SkillGapReport = ({ missingKeywords, matchedKeywords, jobTitle, company }) => {
  const [showLearningResources, setShowLearningResources] = useState(false);

  if (!missingKeywords || missingKeywords.length === 0) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
            <span className="text-xl">🎉</span>
          </div>
          <div>
            <h3 className="font-semibold text-green-800">Perfect Match!</h3>
            <p className="text-sm text-green-600">
              Your resume covers all major keywords for this role.
            </p>
          </div>
        </div>
        {matchedKeywords?.length > 0 && (
          <p className="text-sm text-green-700">
            <strong>{matchedKeywords.length}</strong> keywords matched from the job description.
          </p>
        )}
      </div>
    );
  }

  // Categorize missing keywords by importance
  const criticalKeywords = missingKeywords.slice(0, Math.ceil(missingKeywords.length / 2));
  const niceToHaveKeywords = missingKeywords.slice(Math.ceil(missingKeywords.length / 2));

  // Generate suggestions based on missing keywords
  const generateSuggestion = (keyword) => {
    const lowerKeyword = keyword.toLowerCase();

    // Technical skills
    if (lowerKeyword.includes('python') || lowerKeyword.includes('javascript') || lowerKeyword.includes('java')) {
      return `Add a project or bullet point showcasing your ${keyword} experience`;
    }
    if (lowerKeyword.includes('aws') || lowerKeyword.includes('cloud') || lowerKeyword.includes('azure')) {
      return `Mention cloud-based projects or certifications with ${keyword}`;
    }
    if (lowerKeyword.includes('sql') || lowerKeyword.includes('database') || lowerKeyword.includes('mongodb')) {
      return `Include database work in your experience bullets`;
    }
    if (lowerKeyword.includes('api') || lowerKeyword.includes('rest') || lowerKeyword.includes('graphql')) {
      return `Describe API development or integration projects`;
    }
    if (lowerKeyword.includes('docker') || lowerKeyword.includes('kubernetes') || lowerKeyword.includes('ci/cd')) {
      return `Add DevOps experience or deployment automation work`;
    }

    // Soft skills / methodologies
    if (lowerKeyword.includes('agile') || lowerKeyword.includes('scrum')) {
      return `Mention agile/scrum methodology in your work experience`;
    }
    if (lowerKeyword.includes('leadership') || lowerKeyword.includes('team')) {
      return `Highlight team collaboration or leadership responsibilities`;
    }
    if (lowerKeyword.includes('communication')) {
      return `Add cross-functional collaboration examples`;
    }

    // Default suggestion
    return `Consider adding ${keyword} to your skills or experience section`;
  };

  // Generate learning resource URL
  const getLearningUrl = (keyword) => {
    const lowerKeyword = keyword.toLowerCase().replace(/\s+/g, '+');
    return `https://www.coursera.org/search?query=${lowerKeyword}`;
  };

  return (
    <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-amber-200">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
              <ExclamationTriangleIcon className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 text-lg">Skill Gap Report</h3>
              <p className="text-sm text-amber-700">
                {missingKeywords.length} skill{missingKeywords.length !== 1 ? 's' : ''} missing for{' '}
                <span className="font-medium">{jobTitle}</span>
                {company && <span> at {company}</span>}
              </p>
            </div>
          </div>
          <div className="text-right">
            <span className="text-2xl font-bold text-amber-600">
              {Math.round((matchedKeywords?.length || 0) / ((matchedKeywords?.length || 0) + missingKeywords.length) * 100) || 0}%
            </span>
            <p className="text-xs text-gray-500">Skill Match</p>
          </div>
        </div>
      </div>

      {/* Critical Skills */}
      {criticalKeywords.length > 0 && (
        <div className="p-6 border-b border-amber-100">
          <h4 className="font-medium text-red-700 mb-4 flex items-center gap-2">
            <span className="w-2 h-2 bg-red-500 rounded-full"></span>
            Critical Skills ({criticalKeywords.length})
          </h4>
          <div className="space-y-4">
            {criticalKeywords.map((keyword, i) => (
              <div key={i} className="bg-white rounded-lg p-4 shadow-sm">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <span className="inline-block px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium mb-2">
                      {keyword}
                    </span>
                    <p className="text-sm text-gray-600 flex items-start gap-2">
                      <LightBulbIcon className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      {generateSuggestion(keyword)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Nice to Have Skills */}
      {niceToHaveKeywords.length > 0 && (
        <div className="p-6 border-b border-amber-100">
          <h4 className="font-medium text-amber-700 mb-4 flex items-center gap-2">
            <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
            Nice to Have ({niceToHaveKeywords.length})
          </h4>
          <div className="flex flex-wrap gap-2">
            {niceToHaveKeywords.map((keyword, i) => (
              <span
                key={i}
                className="inline-block px-3 py-1.5 bg-amber-100 text-amber-800 rounded-full text-sm"
              >
                {keyword}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Learning Resources (Expandable) */}
      <div className="p-6">
        <button
          onClick={() => setShowLearningResources(!showLearningResources)}
          className="flex items-center gap-2 text-primary-600 font-medium hover:text-primary-700 transition-colors"
        >
          <AcademicCapIcon className="h-5 w-5" />
          Learn These Skills
          {showLearningResources ? (
            <ChevronUpIcon className="h-4 w-4" />
          ) : (
            <ChevronDownIcon className="h-4 w-4" />
          )}
        </button>

        {showLearningResources && (
          <div className="mt-4 space-y-2">
            <p className="text-sm text-gray-600 mb-3">
              Take courses to strengthen your profile:
            </p>
            <div className="grid sm:grid-cols-2 gap-2">
              {missingKeywords.slice(0, 6).map((keyword, i) => (
                <a
                  key={i}
                  href={getLearningUrl(keyword)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 p-3 bg-white rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors text-sm"
                >
                  <span className="text-gray-700">Learn {keyword}</span>
                  <span className="text-primary-600">→</span>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Action CTA */}
      <div className="bg-primary-600 p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-semibold">Don&apos;t have these skills yet?</h4>
            <p className="text-sm text-primary-100">
              Our AI adds keywords naturally using your existing experience.
            </p>
          </div>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="px-4 py-2 bg-white text-primary-600 rounded-lg font-medium hover:bg-primary-50 transition-colors whitespace-nowrap"
          >
            Re-tailor Resume
          </button>
        </div>
      </div>
    </div>
  );
};

SkillGapReport.propTypes = {
  missingKeywords: PropTypes.arrayOf(PropTypes.string),
  matchedKeywords: PropTypes.arrayOf(PropTypes.string),
  jobTitle: PropTypes.string,
  company: PropTypes.string
};

SkillGapReport.defaultProps = {
  missingKeywords: [],
  matchedKeywords: [],
  jobTitle: 'this role',
  company: ''
};

export default SkillGapReport;
